import { useAtom } from 'jotai'
import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTranslate } from '@repo/unicity'
import CreditCardIcon from '@/core/assets/creditcard-icons/CreditCardIcon.svg?react'
import VisaIcon from '@/core/assets/creditcard-icons/VISA.svg?react'
import MastercardIcon from '@/core/assets/creditcard-icons/MC.svg?react'
import cashOnDeliveryIcon from '@/core/assets/creditcard-icons/cash-on-delivery.png'
import { color } from '@/core/theme/tokens'
import { taiwanPaymentMethodAtom, type TaiwanPaymentMethod } from '../../../stores/taiwan.store'

// Figma 689:6280 (Payment Card). Subtitle uses raw #003764 (design style, no scale token).
const SUBTITLE_TEXT = '#003764'

const METHODS: {
    key: TaiwanPaymentMethod
    label: string
    Icon?: React.FC<React.SVGProps<SVGSVGElement>>
    iconSrc?: string
    brands?: boolean
}[] = [
    { key: 'creditcard', label: 'credit_card', Icon: CreditCardIcon, brands: true },
    { key: 'cashOnDelivery', label: 'cash_on_delivery', iconSrc: cashOnDeliveryIcon }
]

// Radio — Figma 46:9: 20px, 1.5px gray/300 border, filled with brand when checked.
const RadioDot = ({ checked }: { checked: boolean }) => (
    <Box
        sx={{
            boxSizing: 'border-box',
            width: 20,
            height: 20,
            borderRadius: '10px',
            border: `1.5px solid ${checked ? color.blue[600] : color.gray[300]}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
        }}>
        {checked && <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color.blue[600] }} />}
    </Box>
)

// Payment-method selector (Credit card / Cash on Delivery). The choice is stored
// in taiwanPaymentMethodAtom and drives submit in useTaiwanPayment (COD settles
// directly; credit card redirects to the 2c2p hosted gateway).
export default function TaiwanPaymentMethods() {
    const { translate } = useTranslate()
    const [method, setMethod] = useAtom(taiwanPaymentMethodAtom)

    return (
        <Box data-testid="taiwan-payment-methods">
            <Stack sx={{ gap: '14px', mb: 3 }}>
                <Typography
                    sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 700, letterSpacing: '-0.75px', color: color.blue[600] }}
                    data-testid="taiwan-payment-methods-title">
                    {translate('payment')}
                </Typography>
                <Typography sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 400, color: SUBTITLE_TEXT }} data-testid="taiwan-payment-methods-subtitle">
                    {translate('please_choose_your_payment_method')}
                </Typography>
            </Stack>

            <Stack spacing={2} data-testid="taiwan-payment-methods-options">
                {METHODS.map(({ key, label, Icon, iconSrc, brands }) => {
                    const selected = method === key
                    return (
                        <OptionCard key={key} selected={selected} onClick={() => setMethod(key)} data-testid={`taiwan-payment-method-${key}`}>
                            <RadioDot checked={selected} />
                            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, width: 24, height: 24 }}>
                                {Icon ? (
                                    <Icon width={24} height={24} />
                                ) : iconSrc ? (
                                    <Box component="img" src={iconSrc} alt="" sx={{ width: 24, height: 24, objectFit: 'contain' }} />
                                ) : null}
                            </Box>
                            <Typography sx={{ flex: 1, minWidth: 0, fontSize: 16, lineHeight: '24px', fontWeight: 600, color: color.blue[600] }}>
                                {translate(label)}
                            </Typography>
                            {brands ? (
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }} data-testid="taiwan-payment-method-brands">
                                    <VisaIcon width={24} height={16} />
                                    <MastercardIcon width={24} height={16} />
                                </Stack>
                            ) : null}
                        </OptionCard>
                    )
                })}
            </Stack>
        </Box>
    )
}

// Figma 689:6319 (default) + 689:6653 (selected):
//  - default:  1px gray/200 border, white bg
//  - selected: 2px gray/500 border, blue/50 bg
// Both: radius 12, 16 padding, 16 gap.
const OptionCard = styled(Box, {
    shouldForwardProp: p => p !== 'selected'
})<{ selected: boolean }>`
    display: flex;
    align-items: center;
    gap: 16px;
    border: ${({ selected }) => (selected ? `2px solid ${color.gray[500]}` : `1px solid ${color.gray[200]}`)};
    border-radius: 12px;
    padding: 16px;
    cursor: pointer;
    background: ${({ selected }) => (selected ? color.blue[50] : color.white)};
    transition:
        border-color 0.2s,
        background-color 0.2s;
`
